import React, { useEffect } from 'react';
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  Field,
  Input,
  Spinner,
} from '@billda/design-system';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { useWhat3WordsValidation } from '../../../../hooks/useWhat3WordsValidation';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';
import { normalizeWhat3WordsAddress } from '../../../../validation/editSiteSchema';

interface What3WordsFieldProps {
  /** data-testid for the underlying input (defaults to the import stepper id) */
  testId?: string;
}

const What3WordsField: React.FC<What3WordsFieldProps> = ({
  testId = 'site-what3words-input',
}) => {
  const form = useFormContext<SiteImportFormData>();
  const { control, setValue } = form;
  const { errors } = useFormState({ control });

  const what3words = useWatch({ control, name: 'what3words' }) ?? '';

  const { validateAddress, isChecking: w3wChecking } =
    useWhat3WordsValidation(form);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = normalizeWhat3WordsAddress(event.target.value);
    setValue('what3words', value, { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (what3words && /^[a-zA-Z]+\.[a-zA-Z]+\.[a-zA-Z]+$/.test(what3words)) {
      validateAddress(what3words);
    }
  }, [what3words, validateAddress]);

  const getAdornment = () => {
    if (!what3words) {
      return null;
    }
    if (w3wChecking) {
      return <Spinner size={20} />;
    }
    if (errors.what3words) {
      return (
        <ErrorOutlineIcon
          className="size-5 text-content-error"
          aria-hidden="true"
        />
      );
    }
    if (!w3wChecking) {
      return (
        <CheckCircleOutlineIcon
          className="size-5 text-content-success"
          aria-hidden="true"
        />
      );
    }
    return null;
  };

  const getHelperText = () => {
    if (!what3words) {
      return 'Optional - Three words separated by dots (e.g., filled.count.soap)';
    }
    if (errors.what3words) {
      return errors.what3words.message?.toString();
    }
    if (w3wChecking) {
      return 'Checking address...';
    }
    const lat = form.getValues('latitude');
    const lng = form.getValues('longitude');
    if (lat != null && lng != null) {
      return `Valid (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
    }
    return 'Valid address';
  };

  return (
    <Field
      label="What3Words Address"
      error={errors.what3words?.message?.toString()}
      hint={errors.what3words ? undefined : getHelperText()}
    >
      <Input
        value={what3words}
        onChange={handleChange}
        placeholder="filled.count.soap"
        invalid={Boolean(errors.what3words)}
        startAdornment={
          <span className="font-mono text-content-secondary">{'///'}</span>
        }
        endAdornment={getAdornment()}
        data-testid={testId}
      />
    </Field>
  );
};

export default What3WordsField;
